import React, { useEffect, useState } from 'react';
import { gql } from '@apollo/client';
import './NetworkStats.css';

/**
 * Network-wide vesting stats, across BOTH chains.
 *
 * The rest of this dashboard answers "what is MY vesting" — it looks a wallet up
 * by id or beneficiary. This folds every lock contract instead. There is no
 * aggregate entity on either subgraph, so it pages the full set and sums
 * client-side (~2.4k L1 + ~0.7k L2 wallets, four requests).
 *
 * FIELD SEMANTICS — read GraphTokenLock.sol before changing anything here:
 *
 *   tokensReleased   `release()` TRANSFERS TOKENS OUT to the beneficiary and
 *                    adds to this. It means "already claimed and gone", NOT
 *                    "unlocked and sitting in the contract". Every large lock
 *                    contract holds a zero GRT balance on chain, which is the
 *                    observable confirmation of this.
 *   tokensWithdrawn  `withdrawSurplus()` — recovering tokens ACCIDENTALLY SENT
 *                    to the contract, above the amount it manages. Unrelated to
 *                    vesting. Do not net it against anything.
 *   managedAmount    the total the schedule covers.
 *
 * So the only real remaining overhang is `managedAmount - tokensReleased`.
 * An earlier version of this file reported `released - withdrawn` as "vested but
 * unclaimed" and put 4.55B GRT on screen. That subtracts two unrelated
 * quantities and the number was meaningless.
 */
const PAGE = 1000;
const MAX_PAGES = 8; // ~8k wallets; a hard stop so a schema change cannot spin forever

const GET_PAGE = gql`
  query VestingPage($skip: Int!) {
    tokenLockWallets(first: ${PAGE}, skip: $skip, orderBy: id) {
      managedAmount
      tokensReleased
      tokensWithdrawn
      tokensRevoked
      endTime
      revocable
      transferredToL2
    }
  }
`;

const WEI = 1e18;
const toGrt = (v) => Number(v || 0) / WEI;

const compact = (n) => {
  const a = Math.abs(n);
  if (a >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return n.toFixed(0);
};
const full = (n) => n.toLocaleString('en-US', { maximumFractionDigits: 0 });

function fold(wallets) {
  let managed = 0, released = 0, withdrawn = 0, revoked = 0, onL2 = 0, revocable = 0;
  const byYear = new Map();
  for (const w of wallets) {
    const m = toGrt(w.managedAmount);
    const r = toGrt(w.tokensReleased);
    managed += m;
    released += r;
    withdrawn += toGrt(w.tokensWithdrawn);
    revoked += toGrt(w.tokensRevoked);
    if (w.transferredToL2) onL2 += 1;
    if (w.revocable === 'Enabled') revocable += 1;

    // Unlock calendar keyed on the amount NOT yet released — a wallet that has
    // fully released contributes nothing left to unlock, so bucketing all of
    // managedAmount by end year would overstate the forward overhang.
    const stillLocked = Math.max(m - r, 0);
    const t = Number(w.endTime || 0);
    if (t > 0 && stillLocked > 0) {
      const y = new Date(t * 1000).getUTCFullYear();
      byYear.set(y, (byYear.get(y) || 0) + stillLocked);
    }
  }
  return {
    count: wallets.length,
    managed, released, withdrawn, revoked, onL2, revocable,
    stillLocked: Math.max(managed - released, 0),
    byYear: [...byYear.entries()].sort((a, b) => a[0] - b[0]),
  };
}

/** Fold two chains into one view. */
function merge(a, b) {
  const y = new Map(a.byYear);
  for (const [k, v] of b.byYear) y.set(k, (y.get(k) || 0) + v);
  return {
    count: a.count + b.count,
    managed: a.managed + b.managed,
    released: a.released + b.released,
    withdrawn: a.withdrawn + b.withdrawn,
    revoked: a.revoked + b.revoked,
    onL2: b.count,
    revocable: a.revocable + b.revocable,
    stillLocked: a.stillLocked + b.stillLocked,
    byYear: [...y.entries()].sort((x, z) => x[0] - z[0]),
    l1: a, l2: b,
  };
}

/** Single horizontal stacked bar: how the managed total actually splits. */
function CompositionBar({ parts, total }) {
  const W = 1000, H = 54, GAP = 2;
  let x = 0;
  return (
    <div className="ns-scroll">
      <svg className="ns-svg" viewBox={`0 0 ${W} ${H + 30}`} role="img"
           aria-label="Composition of all managed GRT">
        {parts.map((p, i) => {
          const w = total > 0 ? (p.value / total) * W : 0;
          const seg = (
            <g key={p.key}>
              <rect className="ns-seg ns-bar" x={x} y={0} width={Math.max(w - GAP, 0)} height={H}
                    fill={`var(--series-${i + 1})`} />
              {/* Direct labels on every segment wide enough — the light-mode
                  contrast WARN from the validator makes these mandatory. */}
              {w > 90 && (
                <text className="ns-val" x={x + 10} y={H + 20}>
                  {compact(p.value)} · {total > 0 ? ((p.value / total) * 100).toFixed(1) : 0}%
                </text>
              )}
            </g>
          );
          x += w;
          return seg;
        })}
      </svg>
    </div>
  );
}

/** Vertical bars: GRT still locked, by the year its schedule ends. */
function UnlockBars({ data }) {
  if (!data.length) return <p className="ns-note">No wallets with tokens still locked.</p>;
  const W = 1000, H = 260, PAD_L = 8, PAD_B = 44, PAD_T = 26;
  const max = Math.max(...data.map((d) => d[1]));
  const slot = (W - PAD_L) / data.length;
  const bw = Math.min(slot * 0.62, 76);
  const plot = H - PAD_B - PAD_T;
  return (
    <div className="ns-scroll">
      <svg className="ns-svg" viewBox={`0 0 ${W} ${H}`} role="img"
           aria-label="GRT still locked by vesting end year">
        {[0, 0.5, 1].map((f) => (
          <line key={f} className="ns-grid" x1={PAD_L} x2={W} y1={PAD_T + plot * (1 - f)} y2={PAD_T + plot * (1 - f)} />
        ))}
        {data.map(([year, val], i) => {
          const h = max > 0 ? (val / max) * plot : 0;
          const cx = PAD_L + slot * i + slot / 2;
          return (
            <g key={year}>
              <rect className="ns-bar" x={cx - bw / 2} y={PAD_T + plot - h} width={bw} height={h}
                    fill="var(--series-2)" />
              <text className="ns-val" x={cx} y={PAD_T + plot - h - 7} textAnchor="middle">{compact(val)}</text>
              <text x={cx} y={H - PAD_B + 22} textAnchor="middle">{year}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export default function NetworkStats({ client, l2Client }) {
  const [state, setState] = useState({ status: 'loading' });

  useEffect(() => {
    let live = true;
    (async () => {
      const pageAll = async (c) => {
        const out = [];
        for (let p = 0; p < MAX_PAGES; p += 1) {
          const { data } = await c.query({
            query: GET_PAGE,
            variables: { skip: p * PAGE },
            fetchPolicy: 'no-cache',
          });
          const page = data?.tokenLockWallets ?? [];
          out.push(...page);
          if (page.length < PAGE) break;
        }
        return out;
      };
      try {
        // Vesting exists on BOTH chains — L1 contracts and the L2 wallets that
        // migrated. Reading only mainnet silently omits the migrated set.
        const [l1, l2] = await Promise.all([pageAll(client), pageAll(l2Client)]);
        if (live) setState({ status: 'ready', stats: merge(fold(l1), fold(l2)) });
      } catch (e) {
        // Surface it. An empty dashboard and a failed fetch look identical and
        // are very different problems.
        if (live) setState({ status: 'error', message: e.message });
      }
    })();
    return () => { live = false; };
  }, [client, l2Client]);

  if (state.status === 'loading') {
    return <div className="ns-root"><p className="ns-state">Folding every vesting contract…</p></div>;
  }
  if (state.status === 'error') {
    return <div className="ns-root"><p className="ns-state">Couldn’t load network stats: {state.message}</p></div>;
  }

  const s = state.stats;
  // Only two states partition managedAmount: released (gone to beneficiaries)
  // and still locked. `tokensWithdrawn` is surplus recovery and is NOT a slice
  // of this total, so it is reported as a separate tile, never as a segment.
  const parts = [
    { key: 'released', label: 'Released to beneficiaries', value: s.released },
    { key: 'locked', label: 'Still locked', value: s.stillLocked },
  ];

  return (
    <div className="ns-root">
      <div className="ns-hero">
        <div className="ns-hero-value">{compact(s.stillLocked)} GRT</div>
        <div className="ns-hero-label">still locked in vesting contracts</div>
        <p className="ns-hero-sub">
          Across {full(s.count)} contracts covering {compact(s.managed)} GRT on L1 and Arbitrum.
          The other {compact(s.released)} has already been released to beneficiaries.
        </p>
      </div>

      <div className="ns-tiles">
        <div className="ns-tile"><div className="ns-tile-value">{full(s.count)}</div><div className="ns-tile-label">Vesting contracts</div></div>
        <div className="ns-tile"><div className="ns-tile-value">{compact(s.managed)}</div><div className="ns-tile-label">GRT under vesting</div></div>
        <div className="ns-tile"><div className="ns-tile-value">{((s.released / s.managed) * 100).toFixed(1)}%</div><div className="ns-tile-label">Vested to date</div></div>
        <div className="ns-tile"><div className="ns-tile-value">{full(s.l2.count)}</div><div className="ns-tile-label">Contracts on Arbitrum</div></div>
        <div className="ns-tile"><div className="ns-tile-value">{s.revocable}</div><div className="ns-tile-label">Revocable contracts</div></div>
        <div className="ns-tile"><div className="ns-tile-value">{compact(s.withdrawn)}</div><div className="ns-tile-label">Surplus recovered (not vesting)</div></div>
      </div>

      <div className="ns-card">
        <h3>Where the {compact(s.managed)} GRT stands</h3>
        <p className="ns-note">
          Released means <em>already transferred out</em> to the beneficiary — <code>release()</code>
          moves the tokens, which is why lock contracts hold a zero GRT balance on chain.
        </p>
        <CompositionBar parts={parts} total={s.managed} />
        <div className="ns-legend">
          {parts.map((p, i) => (
            <span className="ns-legend-item" key={p.key}>
              <span className="ns-swatch" style={{ background: `var(--series-${i + 1})` }} />
              {p.label}
            </span>
          ))}
        </div>
        {/* Table view: the relief the validator's contrast WARN requires, and
            the thing anyone checking a number will actually want. */}
        <table className="ns-table">
          <thead><tr><th>State</th><th>GRT</th><th>Share</th></tr></thead>
          <tbody>
            {parts.map((p) => (
              <tr key={p.key}>
                <td>{p.label}</td>
                <td>{full(p.value)}</td>
                <td>{((p.value / s.managed) * 100).toFixed(2)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="ns-card">
        <h3>Still locked, by vesting end year</h3>
        <p className="ns-note">
          Counts only tokens not yet released, so a contract that has fully released adds nothing.
          A past year means a schedule whose term has ended while tokens remain unreleased.
        </p>
        <UnlockBars data={s.byYear} />
      </div>
    </div>
  );
}
